import React from 'react';
import { Card, CardBody, Col, Container, Row } from 'reactstrap';
import { Address } from '../../models/Address.interface';
import { ContractTemplateProps } from './Contract.interface';


export const ContractTemplate: React.FC<ContractTemplateProps> = (data) => {
  console.log("ContractTemplate: ", data);
  
  const renderField = (value: string | number | undefined) => {
    const displayValue = value !== undefined ? value.toString() : '';
    return (
      <span className="contract-field contract-field-centered">
        {displayValue}
      </span>
    );
  };

  const calculateAmountOwed = (percentage: number): string => {
    let totalPrice: number;

    if (typeof data.contractPrice === 'string') {
      totalPrice = parseFloat(data.contractPrice.replace(/[^0-9.-]+/g, ""));
    } else if (typeof data.contractPrice === 'number') {
      totalPrice = data.contractPrice;
    } else {
      console.error('Invalid contract price:', data.contractPrice);
      return 'N/A';
    }

    if (isNaN(totalPrice)) {
      console.error('Invalid contract price:', data.contractPrice);
      return 'N/A';
    }

    return (totalPrice * (percentage / 100)).toFixed(2);
  };

  const initialDeposit = data.paymentSchedule.length > 0 ? calculateAmountOwed(data.paymentSchedule[0].percentage) : 'N/A';

  const renderSignature = (role: 'owner' | 'contractor') => {
    const signatureUrl = role === 'owner' ? data.ownerSignature : data.contractorSignature;
    const name = role === 'owner' ? data.ownerName : data.contractorName;

    return (
      <div style={{ display: 'inline-block', width: '200px', marginLeft: '10px' }}>
        <div style={{
          borderBottom: '1px solid black',
          minHeight: '40px'
        }}>
          {signatureUrl && (
            <img src={signatureUrl} alt={`${role} Signature`} style=
              {{
                width: '200px',
                //maxWidth: '100%',
                //maxHeight: '40px'
              }} />
          )}
        </div>
        <div>{name}</div>
      </div>
    );
  };

  const renderSignatureDate = (role: 'owner' | 'contractor') => {
    const signatureDate = role === 'owner' ? data.ownerSignatureDate : data.contractorSignatureDate;

    return (
      <div style={{ display: 'inline-block', width: '200px', marginLeft: '10px', borderBottom: '1px solid black' }}>
        {signatureDate && new Date(signatureDate).toLocaleString(undefined, {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        })}
      </div>
    );
  };

  return (
    <React.Fragment>
      <div className="contract-document">
        <h2 className="contract-title">RESIDENTIAL CONSTRUCTION CONTRACT</h2>

        <p>This Residential Construction Contract ("Agreement") is entered into as of the <strong>{renderField(data.contractDate)}</strong>, by and between:</p>

        <Row>
          <Col sm={6}>
            <strong>Owner:</strong>
            <p>
              Name: {renderField(data.ownerName)}<br />
              Address: {renderField(data.ownerAddress.address1)}<br />
              City, State, Zip: {renderField(data.ownerAddress.city)}, {renderField(data.ownerAddress.state)}, {renderField(data.ownerAddress.zipCode)}<br />
              Phone: {renderField(data.ownerPhone)}<br />
              Email: {renderField(data.ownerEmail)}
            </p>
          </Col>
          <Col sm={6}>
            <strong>Contractor:</strong>
            <p>
              Company Name: {renderField(data.contractorName)}<br />
              License Number: {renderField(data.contractorLicense)}<br />
              Address: {renderField(data.contractorAddress.address1)}<br />
              City, State, Zip: {renderField(data.contractorAddress.city)}, {renderField(data.contractorAddress.state)}, {renderField(data.contractorAddress.zipCode)}<br />
              Phone: {renderField(data.contractorPhone)}<br />
              Email: {renderField(data.contractorEmail)}
            </p>
          </Col>
        </Row>
        <strong>1. Scope of Work</strong>
        <p>
          The Contractor agrees to perform the following work on the property located at:<br />
          Address: {renderField(data.propertyAddress.address1)}<br />
          City, State, Zip: {renderField(data.propertyAddress.city)}, {renderField(data.propertyAddress.state)}, {renderField(data.propertyAddress.zipCode)}<br />
          A detailed description of the work (the "Work") is attached as <strong>Exhibit A</strong>. The Work will be performed according to the plans and specifications agreed upon by the parties.
        </p>

        <strong>2. Contract Price</strong>
        <p>
          The Owner agrees to pay the Contractor the total amount of <strong>${renderField(data.contractPrice)}</strong> (the "Contract Price"), which includes all materials, labor, permits, and associated costs unless otherwise stated in <strong>Exhibit B</strong>.
          Payments will be made according to the following schedule:<br />
          - Initial Deposit: ${renderField(initialDeposit)} due Upon signing contract.<br />
          - Progress Payments: due upon completion of each milestone as listed in <strong>Exhibit C</strong>.<br />
        </p>

        <strong>3. Time of Performance</strong>
        <p>
          The Contractor agrees to start the Work on or about {renderField(data.startDate)} and complete the Work by {renderField(data.completionDate)}. The Contractor will not be held responsible for delays caused by factors beyond their control, including weather, labor strikes, material shortages, or government permits.
        </p>

        <strong>4. Changes in the Work</strong>
        <p>
          Any changes to the Work (including but not limited to alterations, additions, or omissions) must be approved in writing by both the Owner and the Contractor. Changes may affect the Contract Price and/or completion date, and those modifications will be reflected in a <strong>Change Order</strong>.
        </p>

        <strong>5. Permits and Licenses</strong>
        <p>
          The Contractor is responsible for obtaining all necessary building permits and licenses required by the local municipality or state. Any fees associated with permits or inspections are included in the Contract Price unless otherwise stated.
        </p>

        <strong>6. Insurance and Liability</strong>
        <p>
          The Contractor agrees to maintain appropriate insurance coverage during the construction period, including:<br />
          - General Liability Insurance<br />
          - Workers' Compensation Insurance<br /><br />
          The Owner agrees to maintain adequate homeowners' insurance during the project. The Contractor is not liable for any damage to the property beyond their control.
        </p>

        <strong>7. Warranties</strong>
        <p>
          The Contractor warrants that all materials used will be new, of good quality, and free from defects. The Contractor also warrants that the Work will be free from defects for a period of {renderField(data.warrantyPeriod)} days from the date of completion. This warranty excludes normal wear and tear and damages caused by misuse or neglect.
        </p>

        <strong>8. Termination</strong>
        <p>
          Either party may terminate this Agreement with {renderField(data.terminationNoticeDays)} days' written notice if the other party breaches any of the material terms. The Contractor is entitled to compensation for Work completed up to the date of termination. The Owner may terminate the contract if the Work is delayed beyond {renderField(data.delayDays)} days without just cause.
        </p>

        <strong>9. Dispute Resolution</strong>
        <p>
          In the event of a dispute, the parties agree to attempt to resolve the issue through negotiation. If negotiation fails, the parties agree to submit the dispute to mediation before filing any legal claims. If mediation fails, the dispute shall be settled by arbitration in accordance with the rules of the American Arbitration Association. Arbitration shall take place in the state where the property is located.
        </p>

        <strong>10. Governing Law</strong>
        <p>
          This Agreement shall be governed by and construed in accordance with the laws of the State of {renderField(data.propertyAddress.state)}. Both parties agree to comply with all applicable federal, state, and local laws, including, but not limited to, building codes and zoning regulations.
        </p>

        <strong>11. Entire Agreement</strong>
        <p>
          This Agreement, along with all attached Exhibits, represents the entire agreement between the parties and supersedes any prior negotiations or agreements. Any amendments or modifications to this Agreement must be in writing and signed by both parties.
        </p>

        <strong>12. Severability</strong>
        <p>
          If any provision of this Agreement is found to be invalid or unenforceable, the remaining provisions shall remain in full force and effect.
        </p>

        <strong>13. Notices</strong>
        <p>
          Any notices required under this Agreement shall be given in writing to the addresses listed above.
        </p>

        {/* Conditional rendering for Section 14 */}
        {data.requireInspection ? (
          <div style={{ textDecoration: 'line-through !important' }}>
            <strong>14. Contingency: Onsite Property Inspection</strong>
            <p>
              The effectiveness of this Agreement is contingent upon both an onsite property inspection and mutual removal of this contingency by both the Owner and Contractor. The Contractor will inspect the property to assess the scope of the work, site conditions, and any potential issues that may arise during construction. Both parties agree that the Contractor shall have <strong>{renderField(data.inspectionDays)}</strong> days from the date of this Agreement to complete the inspection.
            </p>
            <p>
              Following the inspection, both the Owner and Contractor must approve and sign a <strong>Property Inspection Contingency Removal Form</strong>, confirming that the inspection has been satisfactorily completed and that both parties agree to proceed with the contract. If either party does not remove the contingency within <strong>{renderField(data.removalPeriod)} </strong> days, this Agreement shall be null and void, and any deposit paid shall be refunded to the Owner.
            </p>
          </div>
        ) : (
          <>
            <strong>14. Contingency: Onsite Property Inspection</strong>
            <p>
              The effectiveness of this Agreement is contingent upon both an onsite property inspection and mutual removal of this contingency by both the Owner and Contractor. The Contractor will inspect the property to assess the scope of the work, site conditions, and any potential issues that may arise during construction. Both parties agree that the Contractor shall have <strong>{renderField(data.inspectionDays)}</strong> days from the date of this Agreement to complete the inspection.
            </p>
            <p>
              Following the inspection, both the Owner and Contractor must approve and sign a <strong>Property Inspection Contingency Removal Form</strong>, confirming that the inspection has been satisfactorily completed and that both parties agree to proceed with the contract. If either party does not remove the contingency within <strong>{renderField(data.removalPeriod)} </strong> days, this Agreement shall be null and void, and any deposit paid shall be refunded to the Owner.
            </p>
          </>
        )}

        <Row className="mt-5">
          <Col sm={6}>
            <strong>Owner:</strong>


            <div style={{ marginTop: '10px' }}>
              <span style={{ display: 'inline-block', width: '80px' }}>Name:</span>
              {renderField(data.ownerName)}
            </div>
            <div style={{ marginTop: '10px' }}>
              <span style={{ display: 'inline-block', width: '80px' }}>Signature:</span>
              {renderSignature('owner')}
            </div>
            <div style={{ marginTop: '10px' }}>
              <span style={{ display: 'inline-block', width: '80px' }}>Date:</span>
              {renderSignatureDate('owner')}
            </div>
          </Col>
          <Col sm={6}>
            <strong>Contractor:</strong>
            <div style={{ marginTop: '10px' }}>
              <span style={{ display: 'inline-block', width: '80px' }}>Name:</span>
              {renderField(data.contractorName)}
            </div>
            <div style={{ marginTop: '10px' }}>
              <span style={{ display: 'inline-block', width: '80px' }}>Signature:</span>
              {renderSignature('contractor')}
            </div>
            <div style={{ marginTop: '10px' }}>
              <span style={{ display: 'inline-block', width: '80px' }}>Date:</span>
              {renderSignatureDate('contractor')}
            </div>
          </Col>
        </Row>

        <hr className="exhibit-divider" />

        <h3>Exhibits:</h3>
        <ul className="exhibit-list">
          <li><strong>Exhibit A:</strong> Scope of Work</li>
          <li><strong>Exhibit B:</strong> Contract Price Breakdown</li>
          <li><strong>Exhibit C:</strong> Payment Schedule</li>
        </ul>

        <hr className="exhibit-divider" />

        {/* Exhibit A */}
        <div className="page-break"></div>
        <h2>Exhibit A - Scope of Work</h2>
        <p>{data.jobDetail}</p>

        {/* Exhibit B */}
        <div className="page-break"></div>
        <h2>Exhibit B - Cost Breakdown</h2>
        <table>
          <thead>
            <tr>
              <th>Description</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {data.costBreakdown.map((item, index) => (
              <tr key={index}>
                <td>{item.description}</td>
                <td>${item.homeownerAmount}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Exhibit C */}
        <div className="page-break"></div>
        <h2>Exhibit C - Payment Schedule</h2>
        <table>
          <thead>
            <tr>
              <th>Payment Condition</th>
              <th>Percentage</th>
              <th>Amount Owed</th>
            </tr>
          </thead>
          <tbody>
            {data.paymentSchedule.map((item, index) => (
              <tr key={index}>
                <td>{item.condition}</td>
                <td>{item.percentage}%</td>
                <td>${calculateAmountOwed(item.percentage)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </React.Fragment>
  );
};
